/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'helpers/store'
import { closeModal, closeSecondaryModal } from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import UiFormField from 'components/Forms/UiFormField'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiTextarea from 'components/Ui/UiTextarea'
import UiAlert from 'components/Ui/UiAlert'
import OfferRejectionReasonsList from 'components/Offers/EditOfferStatusModalComponents/OfferRejectionReasonsList'
import { Lead } from 'types/entities/Lead'
import { LeadStatus } from 'types/enums/LeadStatus'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'
import LeadsAction from 'store/leads/LeadsAction'
import { LeadCategory } from 'types/enums/LeadCategory'
import { toast } from 'utils/toastUtils'

interface EditLeadStatusModalProps {
    lead: Lead
    rejectionStatus: LeadStatus
    isSecondary?: boolean
    onSuccess?: (lead: Lead) => void
}

const EditLeadStatusModal: React.FC<EditLeadStatusModalProps> = ({
    lead,
    rejectionStatus,
    isSecondary,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const [selectedReasons, setSelectedReasons] = useState<string[]>(lead.extra?.rejection_reasons ?? [])
    const [otherReasonComment, setOtherReasonComment] = useState(lead.extra?.rejection_other_comment ?? '')
    const [rejectionComment, setRejectionComment] = useState(lead.extra?.rejection_comment ?? '')
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const isOtherReasonSelected = selectedReasons.includes('other')

    const toggleReason = (reasonId: string) => {
        setSelectedReasons((prev) =>
            prev.includes(reasonId) ? prev.filter((id) => id !== reasonId) : [...prev, reasonId]
        )
    }

    const handleClose = () => {
        if (isSecondary) {
            dispatch(closeSecondaryModal())
        } else {
            dispatch(closeModal())
        }
    }

    const handleSave = async (closeAfterSave: boolean = false) => {
        if (selectedReasons.length === 0) {
            toast.warning(t('Please select at least one reason for rejection'))
            return
        }
        if (isOtherReasonSelected && !otherReasonComment.trim()) {
            toast.warning(t('Mandatory comment for other reason'))
            return
        }

        if (!showConfirmation) {
            setShowConfirmation(true)
            return
        }

        setIsSubmitting(true)
        try {
            const payload: Partial<Lead> = {
                id: lead.id,
                name: lead.name,
                hall_id: lead.hall_id,
                seller_id: lead.seller_id,
                status: rejectionStatus,
                extra: {
                    rejection_reasons: selectedReasons,
                    rejection_other_comment: otherReasonComment,
                    rejection_comment: rejectionComment,
                },
            }

            const action =
                lead.category === LeadCategory.Opportunity ? OpportunitiesAction : LeadsAction

            const updatedLead = await dispatch(action.fetchCreateOrUpdateLead(payload))
            toast.success(t('Status updated'))

            if (onSuccess) onSuccess(updatedLead)

            if (closeAfterSave) {
                handleClose()
            } else {
                setIsSubmitting(false)
            }
        } catch (e) {
            console.error(e)
            toast.error(t('Failed to update status'))
            setIsSubmitting(false)
        }
    }

    return (
        <UiFormModalShell
            className="EditLeadStatusModal"
            title={t('Rejection Details')}
            showHeader
            footer={
                <UiFormActionsBar
                    primaryText={showConfirmation ? t('Confirm & Save') : t('Save')}
                    onPrimary={() => handleSave(false)}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={() => handleSave(true)}
                    loading={isSubmitting}
                />
            }
        >
            <div className="p-4 flex flex-col gap-4">
                <UiFormInner>
                    <UiFormGrid cols={1}>
                        <UiFormField>
                            <div className="flex flex-col gap-4">
                                <div className="text-base font-bold text-gray-900 dark:text-gray-50">
                                    {t('Reasons for Rejection')}
                                </div>
                                <div className="space-y-2.5 pl-2">
                                    <OfferRejectionReasonsList
                                        selectedReasons={selectedReasons}
                                        isOtherReasonSelected={isOtherReasonSelected}
                                        otherReasonComment={otherReasonComment}
                                        onToggleReason={toggleReason}
                                        onOtherReasonCommentChange={setOtherReasonComment}
                                    />
                                </div>
                            </div>
                        </UiFormField>

                        <UiFormField>
                            <div className="flex flex-col gap-3">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {t('Optional comment')}
                                </label>
                                <UiTextarea
                                    name="rejection_comment"
                                    placeholder={t(
                                        'Optional comment on the rejection (e.g., feedback, comparison, potential for improvement)'
                                    )}
                                    value={rejectionComment}
                                    onChange={(e) => setRejectionComment(e.target.value)}
                                />
                            </div>
                        </UiFormField>
                    </UiFormGrid>
                </UiFormInner>

                {showConfirmation && (
                    <UiAlert
                        variant="warning"
                        title={t('Confirm Rejection')}
                        description={t('You are about to mark this as Rejected. Do you want to proceed?')}
                    />
                )}
            </div>
        </UiFormModalShell>
    )
}

export default EditLeadStatusModal
